/**
 * CreateEmbedModal Component
 * Module 07 - Dashboard UI
 * Section 06 - Embeds Management (Step 6.2)
 * 
 * Modal for creating new embed configurations with comprehensive settings
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AxiosError } from 'axios';
import { Plus, Trash2, Info } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import ConfigEditor from './ConfigEditor';
import { CreateEmbedInput, EmbedSettings } from '../../types';
import { Model } from '../../types';
import { useToast, useEmbeds } from '../../hooks';
import { isValidDomain } from '../../utils/validators';

interface CreateEmbedModalProps {
  open?: boolean;  // Made optional for backwards compatibility
  isOpen?: boolean;  // New prop name
  onClose: () => void;
  onSuccess?: () => void;  // Made optional
  models?: Model[];  // Made optional
  preSelectedModel?: Model;  // NEW: Pre-select model
  prefilledSettings?: EmbedSettings;  // NEW: Pre-fill configuration
}

// Validation schema
const createEmbedSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  modelId: z.string().min(1, 'Model is required'),
});

type FormData = z.infer<typeof createEmbedSchema>;

// Default embed settings
const defaultSettings: EmbedSettings = {
  lighting: {
    preset: 'neutral',
    intensity: 1.0,
  },
  camera: {
    fov: 45,
    initialViewpoint: 'front',
    autoFrame: true,
  },
  environment: {
    background: 'transparent',
    envMapPreset: 'studio',
  },
  controls: {
    enableRotate: true,
    enableZoom: true,
    enablePan: false,
    autoRotate: false,
  },
};

/**
 * CreateEmbedModal provides a comprehensive form for creating new embed configurations
 * Includes model selection, embed naming, viewer configuration, and domain allowlisting
 */
const CreateEmbedModal: React.FC<CreateEmbedModalProps> = ({
  open,
  isOpen,
  onClose,
  onSuccess,
  models = [],
  preSelectedModel,
  prefilledSettings,
}) => {
  // Support both open and isOpen props for backwards compatibility
  const modalOpen = isOpen ?? open ?? false;
  
  const [settings, setSettings] = useState<EmbedSettings>(prefilledSettings || defaultSettings);
  const [domains, setDomains] = useState<string[]>(['']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const { createEmbed } = useEmbeds();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(createEmbedSchema),
  });

  // Initialize with prefilled settings and pre-selected model
  useEffect(() => {
    if (prefilledSettings) {
      setSettings(prefilledSettings);
    }
    
    if (preSelectedModel) {
      setValue('modelId', preSelectedModel.id);
    }
  }, [prefilledSettings, preSelectedModel, setValue]);

  /**
   * Handle form submission
   */
  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Filter out empty domains and validate
      const allowedDomains = domains
        .map((d) => d.trim())
        .filter((d) => d !== '');

      if (allowedDomains.length === 0) {
        showToast('error', 'Please add at least one allowed domain');
        setIsSubmitting(false);
        return;
      }

      const invalidDomains = allowedDomains.filter((d) => !isValidDomain(d));
      if (invalidDomains.length > 0) {
        showToast('error', `Invalid domain(s): ${invalidDomains.join(', ')}`);
        setIsSubmitting(false);
        return;
      }

      const input: CreateEmbedInput = {
        ...data,
        settings,
        allowedDomains,
      };

      // Call embeds service to create embed
      await createEmbed(input);
      
      showToast('success', 'Embed created successfully');
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error: unknown) {
      showToast(
        'error',
        (error as AxiosError<{ message?: string }>).response?.data?.message || 'Failed to create embed'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Reset form and close modal
   */
  const handleClose = () => {
    resetForm();
    setSettings(defaultSettings);
    setDomains(['']);
    setIsSubmitting(false);
    onClose();
  };

  /**
   * Add new domain input field
   */
  const addDomain = () => {
    setDomains([...domains, '']);
  };

  /**
   * Update specific domain
   */
  const updateDomain = (index: number, value: string) => {
    const newDomains = [...domains];
    newDomains[index] = value;
    setDomains(newDomains);
  };

  /**
   * Remove domain from list
   */
  const removeDomain = (index: number) => {
    if (domains.length === 1) {
      showToast('warning', 'At least one domain is required');
      return;
    }
    setDomains(domains.filter((_, i) => i !== index));
  };

  // Filter models to only show ready ones
  // If preSelectedModel is provided, ensure it's in the list
  let readyModels = models.filter((m) => m.status === 'ready');
  
  if (preSelectedModel && !readyModels.find(m => m.id === preSelectedModel.id)) {
    // Add the preselected model if it's not already in the list
    readyModels = [preSelectedModel, ...readyModels];
  }

  return (
    <Modal open={modalOpen} onClose={handleClose} title="Create Embed" size="large">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Imported Settings Badge */}
        {prefilledSettings && (
          <div className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3">
            <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <p className="text-sm text-blue-900">
              Configuration imported from Model Viewer
            </p>
          </div>
        )}

        {/* Basic Information */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Embed Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('name')}
              type="text"
              className="input-field"
              placeholder="Product Viewer - Blue Dress"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              A descriptive name to help you identify this embed
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model <span className="text-red-500">*</span>
            </label>
            <select 
              {...register('modelId')} 
              className="input-field"
              disabled={isSubmitting}
            >
              <option value="">Select a model...</option>
              {readyModels.length === 0 && (
                <option value="" disabled>
                  No ready models available
                </option>
              )}
              {readyModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
            {errors.modelId && (
              <p className="mt-1 text-sm text-red-600">{errors.modelId.message}</p>
            )}
            {readyModels.length === 0 && (
              <p className="mt-1 text-sm text-amber-600">
                ⚠️ No models are ready yet. Upload and process a model first.
              </p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Choose which 3D model to display in this embed
            </p>
          </div>
        </div>

        {/* Configuration Editor */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Viewer Configuration
          </h3>
          <ConfigEditor settings={settings} onChange={setSettings} />
        </div>

        {/* Allowed Domains */}
        <div className="border-t border-gray-200 pt-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Allowed Domains <span className="text-red-500">*</span>
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Specify which domains can embed this viewer (for security)
            </p>
          </div>
          
          <div className="space-y-3">
            {domains.map((domain, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => updateDomain(index, e.target.value)}
                  className="input-field flex-1"
                  placeholder="example.com or *.example.com"
                  disabled={isSubmitting}
                />
                {domains.length > 1 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={() => removeDomain(index)}
                    disabled={isSubmitting}
                    className="flex-shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addDomain}
            disabled={isSubmitting}
            className="mt-3"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Domain
          </Button>

          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-600">
              <strong>Examples:</strong> example.com, shop.example.com, *.example.com (wildcard)
            </p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting || readyModels.length === 0}
          >
            {isSubmitting ? 'Creating...' : 'Create Embed'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateEmbedModal;
